/**
 * How much of its brightness a trail particle keeps as it nears the band.
 *
 * ## Why this exists
 *
 * `skin-trail-range.ts` guarantees that no live particle sits farther from its
 * ship than the skin declares. Enforcing it with nothing else made the removal
 * visible: measured over 337 animation frames of a departure from rest, the
 * particles the band took away were being drawn at a median alpha of 0.493 and
 * as high as **0.780**, which is the maximum the descriptor allows. A particle
 * disappearing at full brightness is a blink, not a wake ending.
 *
 * So the last sliver of the band is where the residue goes out. The band itself
 * does not move: this only changes how bright a particle is on its way to it.
 *
 * ## Where the zone comes from
 *
 * From the descriptor, not from the measurement. `SPATIAL-CONTRACT.md` derived
 * it before any of this was measured: a particle whose CENTRE is exactly on the
 * limit still draws half of itself past it, and that half is
 *
 * ```
 * zone = (scale.max * diameter) / 2
 * ```
 *
 * which is 0.130 diameters for Silver Hunter, 0.150 for Golden Wing and 0.170
 * for Cyber Rainbow - between 7.5% and 8.5% of each skin's own band, and not the
 * same fraction for any two of them. It is the stretch in which a particle has
 * stopped belonging entirely to the band, so it is the stretch in which the
 * fading belongs.
 *
 * ## The two properties this type exists to hold
 *
 * **It never compounds.** Phaser recomputes `particle.alpha` from its own op on
 * every emitter update, so multiplying that value by a factor is applied to a
 * fresh authored number each frame - as long as the emitter really did run
 * first. Rather than trust an ordering, the value this left behind is recorded:
 * if the alpha found next frame is exactly that value, Phaser has not touched
 * it and the base is recovered from here instead of squaring the factor. A
 * particle held at the same distance for a hundred frames keeps the same
 * brightness, not a hundredth of it.
 *
 * **It never brightens again.** The smallest factor a particle has ever reached
 * is kept, so a hull that turns around and comes back does not relight residue
 * it already left behind. Fading is something that happened to that particle,
 * not a function of where the ship is now.
 */
export interface TrailFadeState {
  /** The authored alpha this was computed from, before the factor. */
  readonly base: number;
  /** What was written to the particle, so the next frame can recognise it. */
  readonly applied: number;
  /** The smallest factor this particle has reached. Never rises. */
  readonly factor: number;
}

export interface TrailFadeQuery {
  /** Distance from the owning hull, in world units. */
  readonly distance: number;
  /** `maxLengthHulls * diameter`, in world units. */
  readonly limit: number;
  /** The dissipation zone, in world units, entirely inside the limit. */
  readonly zone: number;
  /** The alpha the particle currently carries. */
  readonly currentAlpha: number;
  /** What this returned for the same particle last frame, or null on its birth. */
  readonly previous: TrailFadeState | null;
}

export function trailFade(query: TrailFadeQuery): TrailFadeState {
  const { distance, limit, zone, currentAlpha, previous } = query;
  /*
   * The base: the authored alpha, recovered rather than assumed.
   *
   * An exact equality on purpose. `applied` is the number this wrote, so the
   * only way to read it back unchanged is for nothing to have recomputed it.
   */
  const base =
    previous !== null && currentAlpha === previous.applied ? previous.base : currentAlpha;

  let factorNow = 1;
  if (zone > 0 && limit > 0) {
    const remaining = (limit - distance) / zone;
    factorNow = remaining <= 0 ? 0 : remaining >= 1 ? 1 : remaining;
  }
  const factor = previous === null ? factorNow : Math.min(previous.factor, factorNow);
  return { base, applied: base * factor, factor };
}
